/**
 * Public Ulule platform stats (`data.ulule.com/stats.json`), for the static About pages that
 * surface them. Fetched at prerender time, embedded in the HTML as JSON, and re-fetched on
 * hydration so a cached page still shows fresh numbers.
 */

export const ULULE_STATS_DATA_ID = 'ulule-stats-data'
export const ULULE_STATS_URL = 'https://data.ulule.com/stats.json'

/** One entry of the feed's `categories` list, reduced to what the Statistiques ledger renders. */
export type UluleCategory = {
  /** French label, since these pages are French-only; falls back to the feed's neutral `name`. */
  name: string
  amountRaisedEur: number
  /**
   * Feed category id, kept so the Statistiques ledger can link a row to its category page.
   * Optional: a feed entry without one still renders, just without a link.
   */
  id?: number
}

export type UluleStats = {
  amountRaisedEur: number
  projectsSuccessCount: number
  updatedAt: string
  usersCount: number
  /**
   * Read only by the Statistiques page. Optional on purpose: a feed that stopped sending them
   * would otherwise fail validation and blank the figures on every other page too.
   */
  currentYearSuccessPercent?: number
  amountAvgEur?: number
  categories?: UluleCategory[]
}

type SourceStats = {
  amount_raised?: { EUR?: unknown }
  projects_success_count?: unknown
  users_count?: unknown
  years?: unknown
  amount_avg?: { EUR?: unknown }
  categories?: unknown
}

/** One entry of the feed's `years` list, reduced to what the success rate reads. */
type SourceYear = {
  year?: unknown
  projects_success_percent?: unknown
  projects_started_count?: unknown
}

/** Picks the French label and the euro amount; drops entries missing either. */
function parseCategories(source: unknown): UluleCategory[] | undefined {
  if (!Array.isArray(source)) return undefined

  const categories = source.flatMap((entry): UluleCategory[] => {
    if (typeof entry !== 'object' || entry === null) return []

    const category = entry as {
      labels?: { fr?: unknown }
      name?: unknown
      amount_raised?: { EUR?: unknown }
      id?: unknown
    }
    const name = typeof category.labels?.fr === 'string' ? category.labels.fr : category.name
    const amountRaisedEur = category.amount_raised?.EUR

    if (typeof name !== 'string' || name === '' || !isNonNegativeNumber(amountRaisedEur)) return []

    return [{ name, amountRaisedEur, ...(typeof category.id === 'number' ? { id: category.id } : {}) }]
  })

  return categories.length === 0 ? undefined : categories
}

/** A year's rate is representative only past this many started projects: a fresh January reads 0 %. */
const MIN_PROJECTS_FOR_YEAR_RATE = 500

/** Success rate of the most recent representative year, from the feed's `years` list. */
function parseCurrentYearSuccessPercent(source: unknown): number | undefined {
  if (!Array.isArray(source)) return undefined

  const years = source.flatMap((entry): { year: number; percent: number }[] => {
    if (typeof entry !== 'object' || entry === null) return []

    const { year, projects_success_percent: percent, projects_started_count: started } = entry as SourceYear

    if (!isNonNegativeNumber(year) || !isNonNegativeNumber(percent)) return []
    if (!isNonNegativeNumber(started) || started < MIN_PROJECTS_FOR_YEAR_RATE) return []

    return [{ year, percent }]
  })

  if (years.length === 0) return undefined

  // Max rather than last: the feed is chronological today, but nothing promises it stays so.
  return years.reduce((latest, entry) => (entry.year > latest.year ? entry : latest)).percent
}

export function parseUluleStats(source: unknown, updatedAt: string): UluleStats | null {
  if (typeof source !== 'object' || source === null) return null

  const normalizedUpdatedAt = normalizeDate(updatedAt)
  if (normalizedUpdatedAt === null) return null

  const sourceStats = source as SourceStats
  const stats = {
    amountRaisedEur: sourceStats.amount_raised?.EUR,
    projectsSuccessCount: sourceStats.projects_success_count,
    updatedAt: normalizedUpdatedAt,
    usersCount: sourceStats.users_count,
  }

  if (!isUluleStats(stats)) return null

  const categories = parseCategories(sourceStats.categories)
  const currentYearSuccessPercent = parseCurrentYearSuccessPercent(sourceStats.years)

  // Optional extras: kept off the required shape so a feed missing them still yields usable stats.
  return {
    ...stats,
    ...(currentYearSuccessPercent !== undefined ? { currentYearSuccessPercent } : {}),
    ...(isNonNegativeNumber(sourceStats.amount_avg?.EUR) ? { amountAvgEur: sourceStats.amount_avg.EUR } : {}),
    ...(categories !== undefined ? { categories } : {}),
  }
}

export function isUluleStats(value: unknown): value is UluleStats {
  if (typeof value !== 'object' || value === null) return false

  const stats = value as Record<string, unknown>
  return (
    isNonNegativeNumber(stats.amountRaisedEur) &&
    isNonNegativeNumber(stats.projectsSuccessCount) &&
    isValidDate(stats.updatedAt) &&
    isNonNegativeNumber(stats.usersCount)
  )
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function isValidDate(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value))
}

function normalizeDate(value: string): string | null {
  const timestamp = Date.parse(value)
  return Number.isNaN(timestamp) ? null : new Date(timestamp).toISOString()
}

/**
 * KissKissBankBank figures folded into the public totals. `stats.json` only counts projects and
 * amounts raised on Ulule itself, so every page that advertises a platform-wide total adds these —
 * they live here so the pages can't drift apart.
 *
 * The project count is KKBB's own public total, as its GraphQL API reported it on 2026-09-16.
 * KKBB no longer takes projects, so neither figure should move again.
 */
const KKBB_PROJECTS_COUNT = 30_012
const KKBB_AMOUNT_RAISED_EUR = 181_571_996

/** Projects financed across Ulule + KissKissBankBank. */
export function getProjectsFinancedTotal(stats: UluleStats): number {
  return stats.projectsSuccessCount + KKBB_PROJECTS_COUNT
}

/** Euros raised across Ulule + KissKissBankBank. */
export function getAmountRaisedEurTotal(stats: UluleStats): number {
  return stats.amountRaisedEur + KKBB_AMOUNT_RAISED_EUR
}

// `usersCount` has NO KissKissBankBank equivalent on purpose: the two communities were never
// merged, so member counts are Ulule-only and are used raw (as the Press page has always done).
// Only projects and amounts are folded. Read `stats.usersCount` directly.

/**
 * Step the advertised project total is floored to before it appears behind "plus de …". Flooring
 * (rather than rounding to nearest) is what keeps the claim true, and a round number reads better
 * than an exact one in a sentence.
 */
const PROJECTS_ROUNDING_STEP = 5_000

/**
 * Projects financed, floored for a "plus de …" claim, so every page making that claim quotes the
 * same number.
 */
export function getProjectsFinancedRounded(stats: UluleStats): number {
  return Math.floor(getProjectsFinancedTotal(stats) / PROJECTS_ROUNDING_STEP) * PROJECTS_ROUNDING_STEP
}

/**
 * Members floored to whole millions and spelled out, for a "plus de …" claim: `5151463` →
 * `5 millions`. The space is a NO-BREAK space so the figure cannot wrap between the digit and its
 * unit, and the unit is singular below two million.
 *
 * Returns null below one million: `isUluleStats` accepts any non-negative count, so a broken feed
 * could otherwise publish "plus de 0 million de membres". Callers drop the figure instead.
 */
export function formatMembersMillions(stats: UluleStats): string | null {
  const millions = Math.floor(stats.usersCount / 1_000_000)
  if (millions < 1) return null

  return `${millions}\u00a0${millions > 1 ? 'millions' : 'million'}`
}

/**
 * Thousands-separated integer, French style: `86167` → `86 167`. The separator is a NO-BREAK space
 * (U+00A0), both because French typography requires it and so a figure never wraps mid-number when
 * its column gets narrow.
 */
export function formatInteger(value: number): string {
  return Math.floor(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
}

/**
 * Read the build-time stats snapshot that `entry-server` embedded in the page as JSON. Returns
 * `null` when the script is absent or unparseable, which callers treat as "no snapshot".
 */
export function readPrerenderedStats(): UluleStats | null {
  const data = document.getElementById(ULULE_STATS_DATA_ID)?.textContent
  if (data === undefined || data === null) return null

  try {
    const stats: unknown = JSON.parse(data)
    return isUluleStats(stats) ? stats : null
  } catch {
    return null
  }
}

const FETCH_TIMEOUT_MS = 5_000

/**
 * Fetch the stats at prerender time. Never throws: a failure logs and returns `null`, and the page
 * ships without figures rather than failing the build — the client refetch then fills them in.
 * `pageId` only labels the warning.
 */
export async function fetchUluleStats(pageId: string): Promise<UluleStats | null> {
  try {
    const response = await fetch(ULULE_STATS_URL, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
    if (!response.ok) {
      console.warn(`${pageId} prerender: stats request returned HTTP ${response.status}; deferring to the client`)
      return null
    }

    const updatedAt = response.headers.get('Last-Modified') ?? new Date().toISOString()
    const stats = parseUluleStats(await response.json(), updatedAt)
    if (stats !== null) return stats

    console.warn(`${pageId} prerender: stats response was malformed; deferring to the client`)
    return null
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error)
    console.warn(`${pageId} prerender: stats request failed (${reason}); deferring to the client`)
    return null
  }
}

/**
 * Serialize the snapshot into the `<script>` tag that `readPrerenderedStats` reads back.
 *
 * `<` is escaped as `\u003c`: category labels are feed-controlled free text, and a label holding
 * `</script>` would otherwise close the block early and inject markup. The escape is transparent to
 * `JSON.parse`, so the reader needs no counterpart.
 */
export function renderUluleStatsScript(stats: UluleStats | null): string {
  const json = JSON.stringify(stats).replace(/</g, '\\u003c')

  return `<script id="${ULULE_STATS_DATA_ID}" type="application/json">${json}</script>`
}
